import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormRequiredAsterisk } from '@/modules/forms/components/Forms';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { Input } from '@/modules/ui/components/input/Input';
import { Label } from '@/modules/ui/components/label/Label';

import type { JobFormValues } from './JobForm';

interface SkillsSectionProps {
  isGenerating: boolean;
  mustHaveSkills: string[];
  niceToHaveSkills: string[];
  onAddSkill: (skill: string, type: 'mustHave' | 'niceToHave') => void;
  onRemoveSkill: (skill: string, type: 'mustHave' | 'niceToHave') => void;
  onGenerateSkills: (title: string, description: string) => void;
}

export const SkillsSection = ({
  mustHaveSkills,
  niceToHaveSkills,
  onAddSkill,
  onRemoveSkill,
  onGenerateSkills,
  isGenerating,
}: SkillsSectionProps) => {
  const form = useFormContext<JobFormValues>();

  const [newMustHaveSkill, setNewMustHaveSkill] = useState('');
  const [newNiceToHaveSkill, setNewNiceToHaveSkill] = useState('');

  // Sync mustHaveSkills state with form value for validation
  useEffect(() => {
    form.setValue(
      'mustHaveSkills',
      mustHaveSkills.length > 0 ? mustHaveSkills : null,
    );
  }, [mustHaveSkills, form]);

  const mustHaveSkillsError = form.formState.errors.mustHaveSkills;

  return (
    <div className="flex gap-6">
      <div className="flex-1">
        <div className="mb-4 flex items-center gap-2">
          <Label>
            <FormRequiredAsterisk />
            Must Have Skills
          </Label>
          {mustHaveSkills.length === 0 && niceToHaveSkills.length === 0 && (
            <RefreshCw
              size={16}
              onClick={() =>
                onGenerateSkills(
                  form.getValues('title'),
                  form.getValues('description'),
                )
              }
              className={`cursor-pointer ${isGenerating ? 'animate-spin' : ''}`}
            />
          )}
        </div>

        <div className="space-y-2">
          {mustHaveSkills.map((skill: string) => (
            <div key={`must-${skill}`} className="flex items-center gap-2">
              <Checkbox
                checked
                onCheckedChange={(checked) => {
                  if (!checked) {
                    onRemoveSkill(skill, 'mustHave');
                  }
                }}
              />
              <span className="text-sm">{skill}</span>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <Checkbox disabled />
            <Input
              placeholder="Add must-have skill"
              value={newMustHaveSkill}
              onChange={(e) => setNewMustHaveSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();

                  onAddSkill(newMustHaveSkill, 'mustHave');
                  setNewMustHaveSkill('');
                }
              }}
            />
          </div>

          {mustHaveSkillsError && (
            <p className="text-destructive text-sm">
              {mustHaveSkillsError.message}
            </p>
          )}
        </div>
      </div>

      <div className="flex-1">
        <Label className="mb-4">Nice To Have Skills</Label>
        <div className="flex flex-col gap-2">
          {niceToHaveSkills.map((skill: string) => (
            <div key={`nice-${skill}`} className="flex items-center gap-2">
              <Checkbox
                checked
                onCheckedChange={(checked) => {
                  if (!checked) {
                    onRemoveSkill(skill, 'niceToHave');
                  }
                }}
              />
              <span className="text-sm">{skill}</span>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <Checkbox disabled />
            <Input
              placeholder="Add nice-to-have skill"
              value={newNiceToHaveSkill}
              onChange={(e) => setNewNiceToHaveSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();

                  onAddSkill(newNiceToHaveSkill, 'niceToHave');
                  setNewNiceToHaveSkill('');
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
